import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import gridListReducer from '../stateReducer/reducer';
import { sortRows, filterRows, getCurrentPage } from '../utils/data-management';
import { columnPropType, tableCategoriesPropType } from '../utils/propTypes';
import { DataGridContext } from './context';
import DataGridEmpty from './DataGridEmpty';
const propTypes = {
  /**
   * Array of category objects for grouping rows
   */
  categories: PropTypes.arrayOf(tableCategoriesPropType),
  /**
   * Supported children components: `Table` or `GridList` for rendering data,
   * `DataGrid.Filters`, `DataGrid.Pagination`, and any custom component
   */
  children: PropTypes.node,
  /**
   * HTML classes to be applied to the container element
   */
  className: PropTypes.string,
  /**
   * Array of columns that define the layout and presentation of data in the grid
   */
  columns: PropTypes.arrayOf(columnPropType).isRequired,
  /**
   * Array of data rows to display
   */
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * Sets the default sort order
   */
  defaultSort: PropTypes.shape({
    key: PropTypes.string.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired
  }),
  /**
   * Message to be shown when there are no rows to display
   */
  emptyMessage: PropTypes.string,
  /**
   * A function to call when the visible data is updated.
   * Receives two arguments: `filteredRows` and `visibleRows`.
   */
  onVisibleDataChange: PropTypes.func,
  /**
   * Shows the empty component
   */
  showEmpty: PropTypes.bool,
  /**
   * Adds a custom sort function to the DataGrid. Expects a key/value pair,
   * where the key is the sort type ('number', 'string', etc.) and the value
   * is the sort function. To leverage this sort function, set the appropriate
   * `sortType` in the column configuration to the key used here.
   *
   * ```
   * // example:
   * const column = {
   *   dataKey: 'fundName',
   *   header: 'Investment',
   *   sortType: 'random',
   *   sortable: true
   * };
   * const sortTypes = {
   *   random: (a, b, dataKey) => {
   *     return Math.random() - 0.5;
   *   }
   * };
   * ```
   */
  sortTypes: PropTypes.objectOf(PropTypes.func)
};
const defaultProps = {
  showEmpty: false,
  onVisibleDataChange: () => {}
};
class DataGrid extends Component {
  constructor(props) {
    super(props);
    this.updateDataInState = state => {
      const {
        sortTypes,
        onVisibleDataChange
      } = this.props;
      const {
        allRows,
        categories,
        columns,
        filter,
        pagination,
        sort
      } = state;
      const filteredRows = sortRows(filterRows(allRows, filter), sort, columns, sortTypes, categories);
      const {
        rows: visibleRows,
        pageCount
      } = getCurrentPage(filteredRows, pagination);
      onVisibleDataChange(filteredRows, visibleRows);
      return {
        ...state,
        filteredRows,
        visibleRows,
        pageCount
      };
    };
    this.dispatchAction = action => {
      this.setState(state => {
        let newState = gridListReducer(state, action);
        newState = this.updateDataInState(newState);
        return newState;
      });
    };
    const {
      data,
      categories: _categories,
      columns: _columns,
      defaultSort,
      showEmpty
    } = props;
    this.state = {
      allRows: data,
      categories: _categories,
      columns: _columns,
      dispatch: this.dispatchAction,
      filteredRows: data,
      showEmpty,
      sort: defaultSort,
      visibleRows: data
    };
  }
  componentDidUpdate(prevProps) {
    const {
      data,
      categories,
      columns
    } = this.props;
    if (data !== prevProps.data || categories !== prevProps.categories || columns !== prevProps.columns) {
      this.setState(state => {
        let newState = {
          ...state,
          allRows: data,
          categories,
          columns,
          filteredRows: data,
          visibleRows: data
        };
        newState = this.updateDataInState(newState);
        return newState;
      });
    }
  }
  render() {
    const {
      className,
      children,
      emptyMessage,
      showEmpty
    } = this.props;
    return /*#__PURE__*/React.createElement(DataGridContext.Provider, {
      value: this.state
    }, /*#__PURE__*/React.createElement("div", {
      className: cx('asc-grid-container', className)
    }, children, showEmpty && /*#__PURE__*/React.createElement(DataGridEmpty, {
      emptyMessage: emptyMessage
    })));
  }
}
DataGrid.propTypes = propTypes;
DataGrid.defaultProps = defaultProps;
export default DataGrid;